/* eslint-disable react/jsx-props-no-spreading */
/**
 * Custom files dropzone
 *
 * @author Sebastiano Lovatin <sebastiano@gestionaleauto.com>
 */

import React from 'react';
import Resizer from 'react-image-file-resizer';
import { v4 as uniqueId } from 'uuid';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { useTranslation } from 'react-i18next';
/**
 * Material UI
 */
import { makeStyles } from '@mui/styles';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import AddAPhotoIcon from '@mui/icons-material/AddAPhoto';

const useStyles = makeStyles((theme) => ({
  // Complete template css
  completeUpload: {
    cursor: 'pointer',
    border: '1px solid #dbdbdb',
    backgroundColor: 'white',
    borderRadius: 3,
    height: 170,
  },
  completeUploadIcon: {
    color: '#727272',
    width: '42px !important',
    height: '42px !important',
    marginTop: 20,
  },
  completeUploadText: {
    color: '#c1c1c1',
    marginTop: 10,
    fontSize: 18,
    [theme.breakpoints.up('md')]: {
      padding: '0px 100px',
    },
  },
  // Smart template css
  smartUpload: {
    cursor: 'pointer',
    display: 'flex',
    [theme.breakpoints.down('sm')]: {
      flexDirection: 'column',
    },
  },
  smartUploadIcon: {
    display: 'flex',
    height: 47,
    width: 47,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    '& svg': {
      margin: 'auto',
      width: '29px !important',
      height: '29px !important',
    },
    [theme.breakpoints.down('sm')]: {
      margin: 'auto',
    },
  },
  smartUploadText: {
    textAlign: 'left',
    paddingLeft: 10,
    [theme.breakpoints.down('sm')]: {
      textAlign: 'center',
    },
  },
  smartUploadTextTitle: {
    fontSize: 16,
    marginBottom: 0,
  },
  smartUploadTextSubtitle: {
    fontSize: 12,
  },
}));

/**
 * @param template {string} Template to render of dropzone
 * @param acceptedFileTypes {array} Files types that user can upload
 * @param attachmentTypeName {string} attachment type to upload
 * @param maxSize {number} Max size of file
 * @param disabled {boolean} Disabled dropzone
 * @param onUploadFiles {function} Called when file is accepted by dropzone, can be uploaded
 *
 * @return {JSX.Element}
 * @constructor
 */
function FileDropzone({
  template,
  acceptedFileTypes,
  attachmentTypeName,
  maxSize,
  disabled,
  onUploadFiles,
}) {
  const { t } = useTranslation();
  const classes = useStyles();

  /**
   * Function for resize an image before send to API
   *
   * @param image {Blob} Image to resize
   * @returns {Promise<unknown>}
   */
  const resizeImage = (image) => new Promise((resolve) => {
    const resizeFormat = image.type === 'image/png' ? 'PNG' : 'JPEG';
    Resizer.imageFileResizer(
      image,
      1920,
      1080,
      resizeFormat,
      80,
      0,
      (uri) => {
        resolve(uri);
      },
      'file',
    );
  });

  const { getRootProps, getInputProps } = useDropzone({
    maxSize,
    disabled,
    accept: acceptedFileTypes,
    onDrop: async (data) => {
      const files = [];
      for (let i = 0; i < data.length; i += 1) {
        let file = data[i];
        if (file.type.indexOf('image') >= 0) {
          // eslint-disable-next-line no-await-in-loop
          file = await resizeImage(file);
        }
        file.key = uniqueId();
        file.loading = true;
        files.push(file);
      }
      onUploadFiles(files, attachmentTypeName);
    },
  });

  return (
    <div {...getRootProps()}>
      <input {...getInputProps()} />
      {
        template === 'complete' && (
          <div className={`${classes.completeUpload} ga-dropzone-complete-upload`}>
            <FileUploadIcon className={`${classes.completeUploadIcon} ga-dropzone-complete-upload-icon`} />
            <p className={`${classes.completeUploadText} ga-dropzone-complete-upload-text`}>
              {t('Drop your documents here or choose the file from your device')}
            </p>
          </div>
        )
      }
      {
        template === 'smart' && (
          <div className={`${classes.smartUpload} ga-dropzone-smart-upload`}>
            <div className={`${classes.smartUploadIcon} ga-dropzone-smart-upload-icon`}>
              <AddAPhotoIcon />
            </div>
            <div className={`${classes.smartUploadText} ga-dropzone-smart-upload-text`}>
              <p className={`${classes.smartUploadTextTitle} ga-dropzone-smart-upload-title`}>
                {t('Attach photos of your vehicle')}
              </p>
              <span className={`${classes.smartUploadTextSubtitle} ga-dropzone-smart-upload-subtitle`}>
                {t('It is possible to upload JPG or JPEG files with a maximum size of 2MB (2048kb)')}
              </span>
            </div>
          </div>
        )
      }
    </div>
  );
}

FileDropzone.propTypes = {
  template: PropTypes.oneOf(['complete', 'smart']),
  acceptedFileTypes: PropTypes.shape({}),
  attachmentTypeName: PropTypes.oneOf(['document', 'image']),
  maxSize: PropTypes.number,
  disabled: PropTypes.bool,
  onUploadFiles: PropTypes.func,
};

FileDropzone.defaultProps = {
  template: 'complete',
  acceptedFileTypes: { 'image/jpeg': [], 'image/png': [] },
  attachmentTypeName: 'image',
  maxSize: null,
  disabled: false,
  onUploadFiles: () => {},
};

export default FileDropzone;
